import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  sanitizeInput,
  validateSecureInput,
} from '../utils/security';

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

const Auth: React.FC = () => {
  const { login, notice } = useAuth();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const validateLoginForm = (): boolean => {
    const newErrors: FormErrors = {};

    const emailError = validateSecureInput(email, t('auth.email'), {
      required: true,
      maxLength: 255,
      validateEmail: true,
    });
    if (emailError) newErrors.email = emailError;

    const passwordError = validateSecureInput(password, t('auth.password'), {
      required: true,
      minLength: 8,
      maxLength: 128,
    });
    if (passwordError) newErrors.password = passwordError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateLoginForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      await login({
        email: sanitizeInput(email).toLowerCase(),
        password,
      });
    } catch (error) {
      let errorMessage = t('common.serverConnectionError');
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3">
            <img
              src="/assets/logos/ID FINAL-06.png"
              alt="NeuroNews"
              className="h-20 object-contain"
            />
          </div>
          <p className="text-[var(--color-muted)] font-medium">
            {t('auth.subtitle')}
          </p>
        </div>

        <div className="bg-[var(--color-card-background)] rounded-[2.5rem] border border-[var(--color-border)] shadow-2xl p-8">
          <h2 className="text-xl font-bold text-[var(--color-text)] mb-8 text-center">
            {t('auth.login')}
          </h2>

          {/* O erro do envio ou, se ele não houver, por que a sessão terminou (TEXTO 6). */}
          {(errors.general ?? notice) && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-2xl" role="alert">
              <p className="text-red-400 text-sm font-medium flex items-center gap-2">
                <i className="fas fa-exclamation-circle"></i>
                {errors.general ?? notice}
              </p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-[var(--color-text)] font-bold mb-2 text-sm">
                {t('auth.email')}
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-4 py-3 bg-[var(--color-background)] border ${
                    errors.email ? 'border-red-500' : 'border-[var(--color-border)]'
                  } rounded-2xl text-[var(--color-text)] placeholder-[var(--color-muted)] focus:outline-none focus:border-[var(--color-primary)] transition-colors`}
                  placeholder={t('auth.emailPlaceholder')}
                  maxLength={255}
                  disabled={isLoading}
                />
                <i className="fas fa-envelope absolute right-4 top-1/2 -translate-y-1/2 text-[var(--color-muted)]"></i>
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1 font-medium">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-[var(--color-text)] font-bold mb-2 text-sm">
                {t('auth.password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full px-4 py-3 bg-[var(--color-background)] border ${
                    errors.password ? 'border-red-500' : 'border-[var(--color-border)]'
                  } rounded-2xl text-[var(--color-text)] placeholder-[var(--color-muted)] focus:outline-none focus:border-[var(--color-primary)] transition-colors pr-12`}
                  placeholder={t('auth.minChars')}
                  maxLength={128}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors"
                  tabIndex={-1}
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1 font-medium">{errors.password}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-4 rounded-2xl font-bold transition-all ${
                isLoading
                  ? 'bg-[var(--color-muted)] text-white cursor-not-allowed'
                  : 'bg-[var(--color-accent)] hover:bg-[var(--color-accent)]/80 text-[var(--color-background)] shadow-lg shadow-[var(--color-accent)]/20'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="fas fa-spinner fa-spin"></i>
                  {t('auth.processing')}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="fas fa-sign-in-alt"></i>
                  {t('auth.enter')}
                </span>
              )}
            </button>
          </form>
        </div>

        <div className="mt-6 text-center">
          <p className="text-[var(--color-muted)] text-xs font-medium">
            <i className="fas fa-shield-alt mr-1"></i>
            {t('auth.securityNote')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
